import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { DirectionProvider } from '@radix-ui/react-direction';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import {
    useSystemConfiguration,
    useSystemStatus,
} from '../api/system-configuration/use-system-configuration';
import { usePermissions } from '../hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { usePriceSocket } from '../hooks/usePriceSocket';
import { useRegisterArea } from '@/contexts/PageTitleContext';
import { AppSplash } from '@/components/AppSplash';
import { useDocumentDirection } from '@/hooks/useDocumentDirection';
import { cn } from '@/lib/utils';
import SideMenu from './crm/SideMenu';
import Header from './crm/Header';
import MobileNav from './crm/MobileNav';
import { buildNavModel, NavItem } from './crm/nav-model';
import { useCrmShellAttribute, useSidebarCollapsed } from './crm/use-crm-shell';

/**
 * CRM (employee) application shell — see docs/CRM_DESIGN_SYSTEM.md.
 *
 *   backdrop (tenant brand glow)
 *   ┌──────────┬──────────────────────────────┐
 *   │  logo    │ top bar: search · chips       │
 *   │ ┌──────┐ │ ┌──────────────────────────┐ │
 *   │ │ glass│ │ │ content container        │ │
 *   │ │ nav  │ │ │ (not on the dashboard)   │ │
 *   │ └──────┘ │ └──────────────────────────┘ │
 *   └──────────┴──────────────────────────────┘
 *
 * Below md the sidebar and top bar give way to MobileNav. Tokens come from
 * `html[data-shell="crm"]` in index.css.
 */
const CRMLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading, logout, user } = useAuth();

    usePriceSocket();
    useRegisterArea('CRM');
    useCrmShellAttribute();
    const { t } = useTranslation('crm');
    const dir = useDocumentDirection();

    const { data: systemConfig } = useSystemConfiguration();
    const { data: systemStatus } = useSystemStatus();
    const { hasPermission } = usePermissions();
    const { collapsed, canToggle, toggle } = useSidebarCollapsed();

    // Route guard: Redirect non-admin users to dashboard if they don't have permissions for current page
    // Only run this check after user is loaded to prevent redirects on page refresh
    useEffect(() => {
        // Don't run permission check if still loading or user not authenticated
        if (isLoading || !isAuthenticated || !user) {
            return;
        }

        if (!(user as any)?.is_tenant_manager) {
            const currentPath = location.pathname;

            // Define route permissions mapping
            const routePermissions: Record<string, string> = {
                '/leads': 'LEADS',
                '/campaigns': 'CAMPAIGNS',
                '/customers': 'CUSTOMERS',
                '/kyc-requests': 'KYC',
                '/trading-history': 'TRADES',
                '/trading-accounts': 'TRADING_ACCOUNTS',
                '/transactions': 'TRANSACTIONS',
                '/users': 'USERS',
                '/roles': 'USERS',
                '/payment-methods': 'PAYMENT_METHODS',
                '/integrations': 'SYSTEM_SETTINGS',
                '/system-configuration': 'SYSTEM_SETTINGS',
                '/instruments': 'SYSTEM_SETTINGS',
                '/audit-logs': 'SYSTEM_SETTINGS',
            };

            // Special case: /notification-center allows NOTIFICATIONS view OR SYSTEM_SETTINGS view
            if (currentPath === '/notification-center') {
                const canAccessNotificationCenter =
                    hasPermission(ModulePermission.NOTIFICATIONS, PermissionAction.VIEW) ||
                    hasPermission(ModulePermission.SYSTEM_SETTINGS, PermissionAction.VIEW);
                if (!canAccessNotificationCenter) {
                    navigate('/', { replace: true });
                }
            } else {
                // Check if current path requires permissions
                const requiredModule = routePermissions[currentPath];
                if (
                    requiredModule &&
                    !hasPermission(requiredModule as any, PermissionAction.VIEW)
                ) {
                    // Redirect to dashboard if user doesn't have permission
                    navigate('/', { replace: true });
                }
            }
        }
    }, [location.pathname, user, hasPermission, navigate, isLoading, isAuthenticated]);

    if (isLoading) {
        return (
            <AppSplash
                message={t('chrome.loadingWorkspace')}
                logoUrl={systemConfig?.system_logo_url || systemConfig?.logo_url}
                name={systemConfig?.system_name}
            />
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/auth/login" state={{ from: location }} replace />;
    }

    const handleLogout = () => {
        logout();
        navigate('/auth/login');
    };

    const canSee = (item: NavItem) => {
        if (item.anyOf) {
            return item.anyOf.some(permission => hasPermission(permission, PermissionAction.VIEW));
        }
        if (item.permission === null) return true;
        return hasPermission(item.permission, PermissionAction.VIEW);
    };
    const model = buildNavModel(canSee);

    const canSearch =
        hasPermission(ModulePermission.CUSTOMERS, PermissionAction.VIEW) ||
        hasPermission(ModulePermission.LEADS, PermissionAction.VIEW);
    const logoUrl = systemConfig?.system_logo_url || systemConfig?.logo_url;
    const systemName = systemConfig?.system_name;
    // Every page but the dashboard sits in a floating container matching
    // the sidebar card; the dashboard's widgets float on the backdrop.
    const isDashboard = location.pathname === '/';

    return (
        // Radix menus, selects and popovers default to LTR unless told the
        // direction, so in Arabic their icons and checks sat on the wrong side.
        <DirectionProvider dir={dir}>
            <div className="crm-backdrop flex h-screen overflow-hidden supports-[height:100dvh]:h-[100dvh]">
                <SideMenu
                    model={model}
                    collapsed={collapsed}
                    canToggle={canToggle}
                    onToggleCollapsed={toggle}
                    logoUrl={logoUrl}
                    systemName={systemName}
                />

                <div className="flex min-w-0 flex-1 flex-col">
                    <MobileNav
                        model={model}
                        canSearch={canSearch}
                        logoUrl={logoUrl}
                        systemName={systemName}
                        userName={user?.name}
                        userEmail={user?.email}
                        onLogout={handleLogout}
                    />
                    <Header
                        canSearch={canSearch}
                        userName={user?.name}
                        userEmail={user?.email}
                        onLogout={handleLogout}
                    />

                    {systemStatus?.maintenance_mode && (
                        <div className="mx-2 mb-2 mt-2 shrink-0 rounded-xl border border-warning/30 bg-warning/10 px-4 py-2.5 md:mt-0">
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="h-2 w-2 shrink-0 rounded-full bg-warning" />
                                    <span className="text-sm font-medium text-foreground">
                                        {t('chrome.maintenanceActive')}
                                    </span>
                                    <span className="truncate text-xs text-muted-foreground">
                                        {systemStatus.maintenance_message}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate('/system-configuration')}
                                    className="shrink-0 text-xs font-medium text-foreground underline-offset-2 hover:underline"
                                >
                                    {t('chrome.manage')}
                                </button>
                            </div>
                        </div>
                    )}

                    <main
                        id="main-content"
                        className={cn(
                            'min-h-0 flex-1 overflow-y-auto',
                            isDashboard
                                ? ''
                                : 'mx-2 mb-2 mt-2 rounded-[calc(var(--radius)+6px)] border border-content-border bg-app md:mt-0'
                        )}
                    >
                        <div className="min-h-full p-4 lg:p-6">
                            <Outlet />
                        </div>
                    </main>
                </div>
            </div>
        </DirectionProvider>
    );
};

export default CRMLayout;
